import React from 'react';
import { Car, Pencil, Trash2, X } from 'lucide-react';
import { Vehicle } from '../../../models/VehicleModel';
import DataTable, { DataTableColumn } from '../../../components/Base/DataTable';
import StatusBadge from '../../../components/Base/StatusBadge';

type Props = {
  data: Vehicle[];
  loading?: boolean;
  onEdit: (vehicle: Vehicle) => void;
  onDelete: (id: number) => void;
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  /** Total en servidor (paginación) */
  totalCount?: number;
  /** Término de búsqueda activo — feedback de filtro */
  searchTerm?: string;
  onClearSearch?: () => void;
};

function formatCreatedParts(value?: string | null): { date: string; time: string } | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return {
    date: d.toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    time: d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
}

function isOperative(status?: string, isActive?: boolean): boolean {
  if (typeof isActive === 'boolean') return isActive;
  return (status || '').toLowerCase() === 'operativo';
}

const VehiclesTable: React.FC<Props> = ({
  data,
  loading,
  onEdit,
  onDelete,
  toolbar,
  footer,
  totalCount,
  searchTerm = '',
  onClearSearch,
}) => {
  const term = searchTerm.trim();
  const shown = data.length;
  const total = totalCount ?? shown;

  const columns: DataTableColumn<Vehicle>[] = [
    {
      id: 'id',
      header: 'ID',
      width: '96px',
      label: 'ID',
      cell: (row) => <span className="data-table__id">{row.id ?? '—'}</span>,
    },
    {
      id: 'license_plate',
      header: 'Vehículo',
      label: 'Vehículo',
      cell: (row) => (
        <div className="data-table__entity">
          <span className="data-table__entity-avatar" aria-hidden>
            <Car size={16} strokeWidth={2.25} />
          </span>
          <span className="data-table__entity-text">
            <span className="data-table__entity-name" title={row.license_plate}>
              {row.license_plate || '—'}
            </span>
            <span className="data-table__entity-sub">
              {row.brand
                ? row.brand
                : row.id != null
                  ? `Ref. #${row.id}`
                  : '—'}
            </span>
          </span>
        </div>
      ),
    },
    {
      id: 'created_at',
      header: 'Creado',
      label: 'Creado',
      width: '148px',
      cell: (row) => {
        const parts = formatCreatedParts(row.created_at);
        if (!parts) return <span className="data-table__muted">—</span>;
        return (
          <span className="data-table__datetime">
            <span className="data-table__date">{parts.date}</span>
            <span className="data-table__time">{parts.time}</span>
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Estado',
      label: 'Estado',
      width: '120px',
      cell: (row) => {
        const operative = isOperative(row.status, row.is_active);
        const label = row.status || (operative ? 'Operativo' : 'Inactivo');
        return (
          <StatusBadge
            label={label}
            tone={operative ? 'success' : 'neutral'}
          />
        );
      },
    },
  ];

  const summary = !loading ? (
    <>
      <span className="data-table__summary-label">
        {term
          ? `${shown} resultado${shown === 1 ? '' : 's'} en esta página`
          : `Mostrando ${shown} de ${total} vehículos`}
      </span>
      {term ? (
        <span className="data-table__summary-chip">
          <Car size={14} strokeWidth={2.25} aria-hidden />
          <span className="data-table__summary-chip-text">Filtro: “{term}”</span>
          {onClearSearch ? (
            <button
              type="button"
              className="data-table__summary-clear"
              onClick={onClearSearch}
              aria-label="Limpiar filtro de búsqueda"
              title="Limpiar filtro"
            >
              <X size={14} strokeWidth={2.5} aria-hidden />
            </button>
          ) : null}
        </span>
      ) : null}
    </>
  ) : null;

  return (
    <DataTable<Vehicle>
      className="vehicles-data-table"
      columns={columns}
      data={data}
      getRowId={(row) => row.id ?? row.license_plate}
      loading={loading}
      emptyMessage={term ? 'Sin vehículos para esta búsqueda' : 'Sin vehículos'}
      emptyDescription={
        term
          ? 'Prueba con otra patente o marca, o limpia el filtro.'
          : 'Crea un nuevo vehículo para comenzar.'
      }
      toolbar={toolbar}
      summary={summary}
      footer={footer}
      actionsHeader="Acciones"
      aria-label="Tabla de vehículos"
      renderActions={(row) => (
        <>
          <button
            type="button"
            className="data-table__action data-table__action--edit"
            onClick={() => onEdit(row)}
            title="Editar vehículo"
            aria-label={`Editar ${row.license_plate || row.brand || 'vehículo'}`}
          >
            <Pencil size={16} strokeWidth={2} aria-hidden />
            <span>Editar</span>
          </button>
          {row.id != null ? (
            <button
              type="button"
              className="data-table__action data-table__action--danger"
              onClick={() => onDelete(Number(row.id))}
              title="Eliminar vehículo"
              aria-label={`Eliminar ${row.license_plate || row.brand || 'vehículo'}`}
            >
              <Trash2 size={16} strokeWidth={2} aria-hidden />
              <span>Eliminar</span>
            </button>
          ) : null}
        </>
      )}
    />
  );
};

export default VehiclesTable;
